import { useEffect, useId, useRef, useState } from 'react'
import { searchPlaces, type Place } from '../lib/geocode'
import { IconClose, IconSearch } from './icons'

interface Props {
  placeholder?: string
  onPick: (place: Place) => void
}

export default function SearchBar({ placeholder = 'Search places…', onPick }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Place[]>([])
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle')
  const [activeIndex, setActiveIndex] = useState(-1)
  const abortRef = useRef<AbortController | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  useEffect(() => {
    abortRef.current?.abort()
    if (query.trim().length < 2) {
      setResults([])
      setStatus('idle')
      setOpen(false)
      setActiveIndex(-1)
      return
    }
    const ctl = new AbortController()
    abortRef.current = ctl
    setStatus('loading')
    setOpen(true)
    setActiveIndex(-1)
    const t = setTimeout(async () => {
      try {
        const found = await searchPlaces(query.trim(), undefined, ctl.signal)
        if (ctl.signal.aborted) return
        setResults(found)
        setStatus(found.length ? 'ready' : 'empty')
        setOpen(true)
      } catch {
        if (ctl.signal.aborted) return
        setResults([])
        setStatus('error')
        setOpen(true)
      }
    }, 300)
    return () => {
      clearTimeout(t)
      ctl.abort()
    }
  }, [query])

  const pick = (place: Place) => {
    onPick(place)
    setQuery('')
    setResults([])
    setStatus('idle')
    setOpen(false)
    setActiveIndex(-1)
    inputRef.current?.blur()
  }

  const clear = () => {
    setQuery('')
    setResults([])
    setStatus('idle')
    setOpen(false)
    setActiveIndex(-1)
    inputRef.current?.focus()
  }

  const menuOpen = open && query.trim().length >= 2

  return (
    <div className={`search-wrap${menuOpen ? ' open' : ''}`}>
      <span className="s-icon">
        <IconSearch />
      </span>
      <input
        ref={inputRef}
        className="field"
        value={query}
        placeholder={placeholder}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => query.trim().length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false)
            inputRef.current?.blur()
            return
          }
          if (!results.length) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActiveIndex((i) => (i + 1) % results.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setOpen(true)
            setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            pick(results[activeIndex >= 0 ? activeIndex : 0])
          }
        }}
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? listId : undefined}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
      />
      {query && (
        <button className="search-clear" type="button" onClick={clear} aria-label="Clear search">
          <IconClose />
        </button>
      )}
      {menuOpen && (
        <div className="search-results" id={listId} role="listbox">
          {status === 'loading' && <div className="search-status"><span className="busy-dot" /> Searching…</div>}
          {status === 'empty' && <div className="search-status">No places found</div>}
          {status === 'error' && <div className="search-status error-text">Search unavailable. Try again.</div>}
          {results.map((r, i) => (
            <button
              key={`${r.lng}-${r.lat}-${r.name}`}
              id={`${listId}-${i}`}
              type="button"
              role="option"
              aria-selected={activeIndex === i}
              className={activeIndex === i ? 'active' : ''}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
            >
              {r.name}
              {r.detail && <span className="sr-detail"> — {r.detail}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
